/**
 * DD0 Reviews — <dd0-reviews> web component (DD0 Pro v1.6)
 *
 * Renders a curated-review widget that FEELS like hundreds/thousands of reviews
 * without shipping thousands of objects: the summary count is typed by the
 * merchant (weightless text), and a pool of ~100 reviews + filters + load-more
 * (+ optional shuffle/loop) gives an endless feel.
 *
 * Data source (resolved lazily, only when scrolled into view — never touches LCP):
 *   1. If a sibling <script type="application/json" data-dd0-reviews> has content
 *      → parse it inline (merchant's own reviews, no network request).
 *   2. Else → fetch(data-bank-url) (the shipped niche bank asset, CDN-cached).
 *
 * Review object: { rating, author, date, verified, text, image } — "image_url"
 * is accepted as an alias for "image".
 *
 * Attributes:
 *   data-bank-url  URL of the niche bank JSON asset (fallback source)
 *   data-batch     cards revealed per "load more" (default 8)
 *   data-shuffle   "true" → randomize order once on load
 *   data-loop      "true" → when exhausted, reshuffle & keep revealing (infinite)
 *   data-summary-from-json  "true" → recompute the sibling summary (average,
 *                  stars, distribution bars) from the merchant's own reviews
 *                  instead of the theme-editor sliders. Only emitted by Liquid
 *                  when there IS inline JSON: deriving a store's rating from
 *                  the canned niche bank would be meaningless.
 */

// Idempotent: this file is enqueued by BOTH the Reviews section and the
// reviews_mini block, so it can load twice on one page. The IIFE scopes the
// top-level const/class so a second <script> can't throw "already declared"
// (a parse-time error the customElements guard below can't catch).
(function () {
const STAR = 'M9.05 2.93c.3-.92 1.6-.92 1.9 0l1.07 3.29a1 1 0 0 0 .95.69h3.46c.97 0 1.37 1.24.59 1.81l-2.8 2.03a1 1 0 0 0-.36 1.12l1.07 3.29c.3.92-.76 1.69-1.54 1.12l-2.8-2.03a1 1 0 0 0-1.18 0l-2.8 2.03c-.78.57-1.83-.2-1.54-1.12l1.07-3.29a1 1 0 0 0-.36-1.12l-2.8-2.03c-.78-.57-.38-1.81.59-1.81h3.46a1 1 0 0 0 .95-.69l1.07-3.29Z';

class DD0Reviews extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-dd0-list]');
    this.moreBtn = this.querySelector('[data-dd0-more]');
    this.filtersEl = this.querySelector('[data-dd0-filters]');
    this.tpl = this.querySelector('[data-dd0-card]');
    this.batch = parseInt(this.dataset.batch, 10) || 8;
    this.loop = this.dataset.loop === 'true';
    this.shuffle = this.dataset.shuffle === 'true';
    this.started = false;

    this.forceVerified = this.dataset.forceVerified === 'true';
    this.sortSel = this.querySelector('[data-dd0-sort]');
    this.curFilter = 'all';
    this.curSort = this.sortSel ? this.sortSel.value : 'featured';

    if (this.moreBtn) this.moreBtn.addEventListener('click', () => this.renderBatch());
    if (this.sortSel) this.sortSel.addEventListener('change', () => { this.curSort = this.sortSel.value; this.applyView(); });

    // Summary from the merchant's own reviews — done HERE, not in the lazy
    // start() below: the summary often sits above the fold, and flipping 4.8 to
    // 4.6 under the shopper's eyes mid-scroll is worse than the work it saves.
    // Cheap by construction: this path only runs with the JSON already inline in
    // the DOM (never for the fetched bank), so it costs a parse, not a request.
    if (this.dataset.summaryFromJson === 'true') {
      const own = this.readInline();
      if (own) this.paintSummary(own.map(normalize).filter((r) => r.text));
    }

    // Defer everything until the section scrolls into view.
    this.io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        this.io.disconnect();
        this.start();
      }
    }, { rootMargin: '200px 0px' });
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  async start() {
    if (this.started) return;
    this.started = true;
    let data = this.readInline();
    if (!data) data = await this.fetchBank();
    if (!Array.isArray(data) || !data.length) {
      this.setAttribute('data-dd0-empty', '');
      return;
    }
    this.all = data.map(normalize).filter((r) => r.text);
    if (this.shuffle) shuffle(this.all);
    this.buildFilters();
    this.applyView();
  }

  readInline() {
    const el = this.querySelector('script[type="application/json"][data-dd0-reviews]');
    if (!el) return null;
    const raw = el.textContent.trim();
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (_) { return null; }
  }

  async fetchBank() {
    const url = this.dataset.bankUrl;
    if (!url) return null;
    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  // Rewrites the server-rendered summary from the reviews themselves: average,
  // star fill and distribution bars. The TOTAL count is deliberately left alone
  // — that number is the merchant's claim about their whole shop, not a count of
  // the reviews pasted here.
  // The summary is a sibling, not a child, in all three surfaces that use this
  // component (Reviews section, reviews_mini block, and the mini's "see all"
  // modal), and in all three it's a DIRECT child of the same parent — hence
  // ':scope >', which keeps the mini from reaching into its own modal.
  paintSummary(list) {
    const box = this.parentElement && this.parentElement.querySelector(':scope > .dd0-rv-summary');
    if (!box || !list.length) return;

    const counts = [0, 0, 0, 0, 0]; // index 0 = 1★
    let sum = 0;
    list.forEach((r) => { counts[r.rating - 1] += 1; sum += r.rating; });
    const avg = Math.round((sum / list.length) * 10) / 10;

    // One decimal, the same shape Liquid prints, so nothing reflows on swap.
    box.querySelectorAll('[data-dd0-avg]').forEach((el) => { el.textContent = avg.toFixed(1); });

    const stars = box.querySelector('.dd0-rv-stars');
    if (stars) {
      // Half-star fill, same rounding as the dd0-rv-stars snippet (no slivers).
      stars.style.setProperty('--rating', String(Math.round(avg * 2) / 2));
      stars.setAttribute('aria-label', avg.toFixed(1) + '/5');
    }

    const pct = percentages(counts, list.length);
    box.querySelectorAll('[data-dd0-dist]').forEach((row) => {
      const p = pct[parseInt(row.dataset.dd0Dist, 10) - 1];
      if (p === undefined) return;
      const fill = row.querySelector('.dd0-rv-dist__fill');
      const label = row.querySelector('.dd0-rv-dist__pct');
      if (fill) fill.style.width = p + '%';
      if (label) label.textContent = p + '%';
    });
  }

  buildFilters() {
    if (!this.filtersEl) return;
    const hasPhoto = this.all.some((r) => r.image);
    const defs = [{ key: 'all', label: this.filtersEl.dataset.labelAll || 'Todas' }];
    if (hasPhoto) defs.push({ key: 'photo', label: this.filtersEl.dataset.labelPhoto || 'Con foto' });
    [5, 4, 3, 2, 1].forEach((s) => {
      if (this.all.some((r) => r.rating === s)) defs.push({ key: 's' + s, label: s + '★' });
    });
    this.filtersEl.textContent = '';
    defs.forEach((d, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dd0-rv-filter';
      btn.textContent = d.label;
      btn.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
      btn.addEventListener('click', () => this.applyFilter(d.key, btn));
      this.filtersEl.appendChild(btn);
    });
  }

  applyFilter(key, btn) {
    this.filtersEl.querySelectorAll('.dd0-rv-filter').forEach((b) => b.setAttribute('aria-pressed', 'false'));
    btn.setAttribute('aria-pressed', 'true');
    this.curFilter = key;
    this.applyView();
  }

  // Filter + sort, then reset and render the first batch.
  applyView() {
    let arr = this.all;
    const f = this.curFilter;
    if (f === 'photo') arr = arr.filter((r) => r.image);
    else if (f && f[0] === 's') arr = arr.filter((r) => r.rating === parseInt(f.slice(1), 10));
    arr = arr.slice();
    if (this.curSort === 'recent') arr.sort((a, b) => a._rank - b._rank);
    else arr.sort((a, b) => ((b.featured ? 1 : 0) - (a.featured ? 1 : 0)) || (b.rating - a.rating) || (a._rank - b._rank)); // featured-first, positive, newest
    this.filtered = arr;
    this.shown = 0;
    this.list.textContent = '';
    this.renderBatch();
  }

  renderBatch() {
    if (!this.tpl || !this.list) return;
    let end = this.shown + this.batch;

    // Loop mode: never run dry — reshuffle and keep going.
    if (this.loop && end >= this.filtered.length && this.filtered.length) {
      const extra = this.filtered.slice();
      shuffle(extra);
      this.filtered = this.filtered.concat(extra);
    }

    const slice = this.filtered.slice(this.shown, end);
    const frag = document.createDocumentFragment();
    slice.forEach((r) => frag.appendChild(this.card(r)));
    this.list.appendChild(frag);
    this.shown += slice.length;

    if (this.moreBtn) {
      const done = !this.loop && this.shown >= this.filtered.length;
      this.moreBtn.hidden = done;
    }
  }

  card(r) {
    const node = this.tpl.content.firstElementChild.cloneNode(true);
    const set = (field, fn) => {
      const el = node.querySelector('[data-field="' + field + '"]');
      if (el) fn(el);
    };
    set('author', (el) => { el.textContent = r.author; });
    set('initial', (el) => { el.textContent = (r.author || '?').trim().charAt(0).toUpperCase(); });
    set('date', (el) => { el.textContent = r.date || ''; if (!r.date) el.remove(); });
    set('verified', (el) => { if (!(this.forceVerified || r.verified)) el.remove(); });
    set('text', (el) => { el.textContent = r.text; });
    set('stars', (el) => { el.innerHTML = starsHTML(r.rating); el.setAttribute('aria-label', r.rating + '/5'); });
    set('image', (el) => {
      if (r.image) { el.src = r.image; el.alt = (r.author || 'Customer') + ' review photo'; }
      else { el.remove(); }
    });
    return node;
  }
}

function starsHTML(rating) {
  let s = '';
  for (let i = 1; i <= 5; i++) {
    s += '<svg viewBox="0 0 20 20" class="dd0-rv-star' + (i <= rating ? ' is-on' : '') +
         '" aria-hidden="true"><path d="' + STAR + '"/></svg>';
  }
  return s;
}

// Whole percentages that always add up to 100 (largest remainder). Rounding each
// bar on its own leaves 99% or 101% on screen, and a summary that doesn't add up
// is exactly what a shopper checking reviews is looking for.
function percentages(counts, total) {
  const raw = counts.map((c) => (c * 100) / total);
  const out = raw.map((v) => Math.floor(v));
  let left = 100 - out.reduce((a, b) => a + b, 0);
  raw
    .map((v, i) => ({ rest: v - Math.floor(v), raw: v, i }))
    // Exact ties happen (62.5 vs 12.5 with 8 reviews). Hand the point to the
    // bigger bar, the way rounding half up would: otherwise 5★ reads 62% while
    // a 3★ bar with the same .5 gets rounded up, which looks arbitrary on screen.
    .sort((a, b) => (b.rest - a.rest) || (b.raw - a.raw))
    .forEach((x) => { if (left > 0) { out[x.i] += 1; left -= 1; } });
  return out;
}

function normalize(r) {
  r = r || {};
  let rating = parseInt(r.rating, 10);
  if (isNaN(rating)) rating = 5;
  rating = Math.max(1, Math.min(5, rating));
  const date = (r.date || '').toString();
  return {
    rating,
    author: (r.author || r.name || 'Customer').toString(),
    date,
    _rank: dateRank(date),
    verified: r.verified === true || r.verified === 'true' || r.verified === 1,
    featured: r.featured === true || r.featured === 'true' || r.featured === 1,
    text: (r.text || r.review || '').toString(),
    image: (r.image || r.image_url || '').toString(),
  };
}

// Approx "days ago" from a date string, for the "Most recent" sort.
// Handles ISO dates + relative EN ("2 weeks ago") / ES ("hace 2 semanas"). Unknown → old.
function dateRank(s) {
  s = (s || '').toLowerCase().trim();
  const t = Date.parse(s);
  if (!isNaN(t)) return (Date.now() - t) / 86400000;
  const m = s.match(/(\d+)\s*(year|month|week|day|a[nñ]o|mes|semana|d[ií]a)/);
  if (m) {
    const n = parseInt(m[1], 10);
    const u = m[2];
    const mult = /year|a[nñ]o/.test(u) ? 365 : /month|mes/.test(u) ? 30 : /week|semana/.test(u) ? 7 : 1;
    return n * mult;
  }
  return 99999;
}

function shuffle(a) {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// See-all modal wiring (event delegation — one listener covers every mini instance).
if (!window.__dd0ReviewsModalWired) {
  window.__dd0ReviewsModalWired = true;
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dd0-open-all]');
    if (opener) {
      const dlg = document.getElementById(opener.getAttribute('data-dd0-open-all'));
      if (dlg && dlg.showModal) {
        dlg.showModal();
        dlg.querySelector('dd0-reviews')?.start?.(); // init the full list on first open
      }
      return;
    }
    if (e.target.closest('[data-dd0-close]')) { e.target.closest('dialog')?.close(); return; }
    if (e.target.tagName === 'DIALOG') e.target.close(); // click on backdrop
  });
}

if (!customElements.get('dd0-reviews')) {
  customElements.define('dd0-reviews', DD0Reviews);
}

})();
